/**
 *  Panel actions module file
**/
import { ACTIONS, MUTATIONS, GETTERS } from './constants'
import mapService from '@/api/mapService'
import { getSubcategoryIcon } from '@/utils/getSubcategoryIcon'
import { getCategoryIcon } from '@/utils/getCategoryIcon'
import { setIconPois } from '@/utils/setIconPois'
import { multiLineString, bbox, lineChunk, bearing } from '@turf/turf'
import { LAYER_ROUTE, LAYER_ROUTE_ARROW, ROUTE_ARROW_CHUNK, UNCATEGORIZED_POIS } from '@/config'
import angle from '@turf/angle'
import { getFilterRoute } from '@/utils/getFilterRoute'
import ROUTING_ACTIONS, { LINKS, PUBLIC_TRANSPORT, SECURITY_CONTROLS, TYPES_HIDDEN, TYPES_HIDDEN_NO_LINKS, ONEWAY } from '@/utils/routing-actions'
import { isSpecialPois } from '@/utils/isSpecialPois'
import { normalizeLowerCase } from '@/utils/matchString'
import { getEventLabelGM } from '@/utils/getEventLabelGM'

export const actions = {
  [ACTIONS.LOAD_FULL_AIRPORT] ({ getters, commit, dispatch }) {
    return Promise.all([
      mapService.getAirport(getters[GETTERS.GET_AIRPORT_ID], getters[GETTERS.GET_LANGUAGE]),
      mapService.getPoisCatalog(getters[GETTERS.GET_LANGUAGE]),
      mapService.getPoisList(getters[GETTERS.GET_AIRPORT_ID], getters[GETTERS.GET_LANGUAGE]),
      mapService.getUncategorizedPoisList(getters[GETTERS.GET_AIRPORT_ID], getters[GETTERS.GET_LANGUAGE]),
      mapService.getTilesBuildingFloor(getters[GETTERS.GET_AIRPORT_ID])
    ]).then(([airport, catalog, pois, uncategorizedPois, tiles]) => {
      // Process airport
      airport.buildings.forEach(building => {
        building.levels.sort((a, b) => b.id - a.id)
      })
      airport.buildings = airport.buildings.sort((a, b) => a.order - b.order)
      commit(MUTATIONS.SAVE_AIRPORT, airport)
      commit(MUTATIONS.SAVE_ROUTE, { z: airport.height })
      dispatch(ACTIONS.RESET_BBOX)

      // Process response
      catalog.forEach(category => {
        category.icon = getCategoryIcon(category.id)
        category.subcategories.forEach(subcategory => {
          const { icon, iconMap } = getSubcategoryIcon(subcategory.id)
          subcategory.icon = icon
          subcategory.iconMap = iconMap
        })
      })

      if (!pois) {
        pois = {
          type: 'FeatureCollection',
          features: []
        }
      }

      if (!uncategorizedPois) {
        uncategorizedPois = {
          type: 'FeatureCollection',
          features: []
        }
      }

      // Process POIS
      pois.features.forEach(poi => {
        const building = getters[GETTERS.GET_BUILDINGS].find(x => x.id === poi.properties.building)
        const { icon, iconMap } = setIconPois(poi, catalog)
        if (building) {
          const level = building.levels.find(x => x.id === poi.properties.level) || {}
          poi.properties.levelName = level.name || poi.properties.level
        }
        poi.properties.icon = icon
        poi.properties.iconMap = iconMap
      })

      // Transition Pois for outdoor ended point
      const transitionPois = uncategorizedPois.features.filter(x => x.properties.subcategories[0] === 0)

      uncategorizedPois.features.forEach(poi => {
        const specialPoi = UNCATEGORIZED_POIS[isSpecialPois(poi.properties.subcategories)]

        if (specialPoi !== undefined) {
          const { icon, iconMap } = setIconPois(poi, catalog)
          poi.properties.icon = icon
          poi.properties.iconMap = iconMap
          const building = getters[GETTERS.GET_BUILDINGS].find(x => x.id === poi.properties.building)

          if (building) {
            const level = building.levels.find(x => x.id === poi.properties.level) || {}
            poi.properties.levelName = level.name || poi.properties.level
          }

          if (!poi.properties.attrs) {
            poi.properties.attrs = {}
          }

          poi.properties.attrs.pat_nombre = specialPoi.name
        }
      })

      const airportCatalog = catalog.reduce((acum, current) => {
        const { subcategories } = current
        current.subcategories = subcategories.filter(subcategory => {
          return [
            ...pois.features,
            ...uncategorizedPois.features
          ].some(poi => poi.properties.subcategories.includes(subcategory.id))
        })

        acum.push(current)

        return acum
      }, [])

      commit(MUTATIONS.SAVE_POIS_CATALOG, airportCatalog)
      commit(MUTATIONS.SAVE_TRANSITION_POIS_LIST, transitionPois)
      commit(MUTATIONS.SAVE_POIS_LIST, pois)
      commit(MUTATIONS.SAVE_UNCATEGORIZED_POIS_LIST, uncategorizedPois)
      // Process cartography
      commit(MUTATIONS.SAVE_CARTOGRAPHY_TILES, tiles.tiles_url)
    })
  },
  [ACTIONS.SET_COORDINATES] ({ getters, commit }, payload) {
    commit(MUTATIONS.SET_COORDINATES, payload)
  },
  [ACTIONS.BLOCK_BBOX] ({ commit }, payload) {
    commit(MUTATIONS.BLOCK_BBOX, payload)
  },
  [ACTIONS.RESET_BBOX] ({ getters, commit }, payload) {
    const coordinates = getters[GETTERS.GET_LONLAT]
    let bbox = getters[GETTERS.GET_AIRPORT]

    if (payload) {
      bbox = payload
    }
    commit(MUTATIONS.SAVE_BBOX, bbox.bbox)
    // Fly to original coordinates if lat, lon parameters are fulfills
    if (coordinates[0] !== undefined && coordinates[0] !== undefined && coordinates[0] !== '' && coordinates[1] !== '') {
      commit(MUTATIONS.SAVE_BBOX, getters[GETTERS.GET_INITIAL_BBOX])
      setTimeout(() => { commit(MUTATIONS.SET_COORDINATES, { coordinates: coordinates, zoom: 19 }) }, 300)
    }
  },
  [ACTIONS.LOAD_POI] ({ getters, commit, dispatch }, payload) {
    commit(MUTATIONS.SAVE_POI, payload)
  },
  [ACTIONS.CLEAR_POI] ({ commit }) {
    commit(MUTATIONS.CLEAR_POI)
  },
  [ACTIONS.LOAD_TILES_BUILDING_FLOOR] ({ getters, commit, dispatch }, payload) {
    return dispatch(ACTIONS.ADD_LAYER, {
      id: payload.id,
      source: 'mvt',
      url: getters[GETTERS.GET_CARTOGRAPHY_TILES],
      data: null,
      layers: payload.layers
    })
  },
  [ACTIONS.LOAD_FLOOR] ({ commit, getters }, payload = {}) {
    commit(MUTATIONS.SAVE_FLOOR, payload)
    if (!getters[GETTERS.HAS_ROUTE] && !getters[GETTERS.GET_BLOCK_BBOX]) {
      const bbox = payload.floor || payload.building || {}
      commit(MUTATIONS.SAVE_BBOX, bbox.bbox)
    }
    commit(MUTATIONS.BLOCK_BBOX, false)
  },
  [ACTIONS.LOAD_ROUTE] ({ getters, commit, dispatch }, payload = {}) {
    const { step, routeMode, distance = 0, duration = 0, reset, error, contextual, i18n } = payload
    let { origin, destination } = payload
    if (contextual) {
      const poiDetail = getters[GETTERS.GET_POI]
      if (poiDetail) {
        if (origin) {
          destination = poiDetail
        } else if (destination) {
          origin = poiDetail
        }
      }
    }

    const route = getters[GETTERS.GET_ROUTE]

    const checkOrigin = !!(getters[GETTERS.GET_ORIGIN] || origin)
    const checkDestination = !!(getters[GETTERS.GET_DESTINATION] || destination)

    if (!route.destinationName && !checkOrigin) {
      dispatch(ACTIONS.SET_DESTINATION_NAME, 'origin')
    } else {
      if (checkOrigin && !checkDestination) {
        dispatch(ACTIONS.SET_DESTINATION_NAME, 'destination')
      }
      if (checkDestination && !checkOrigin) {
        dispatch(ACTIONS.SET_DESTINATION_NAME, 'origin')
      }
      if (checkOrigin && checkDestination && !(route.destinationName || '').includes('step')) {
        dispatch(ACTIONS.SET_DESTINATION_NAME)
      }
    }

    let steps = route.steps

    let _error = error

    steps = steps.filter(x => !x.new)
    let el = step
    if (el && el.geometry) {
      el.geometry.coordinates[2] = hasFloor(el, route.z)
    }

    if (origin || destination) {
      if (destination) {
        destination.geometry.coordinates[2] = hasFloor(destination, route.z)
        el = {
          ...destination,
          isDestination: true
        }

        steps = steps.filter(x => !x.isDestination)

        steps = [
          ...steps,
          el
        ]

        if (i18n) {
          const label = getEventLabelGM(destination, i18n)
          const tag = {
            'eventCategory': 'como llegar',
            'eventAction': 'destino',
            'eventLabel': normalizeLowerCase(label),
            'language': i18n.locale,
            'airportID': getters[GETTERS.GET_AIRPORT_ID]
          }
          window.digitalChange('event', tag)
        }
      }
      if (origin) {
        origin.geometry.coordinates[2] = hasFloor(origin, route.z)

        el = {
          ...origin,
          isOrigin: true
        }

        steps = steps.filter(x => !x.isOrigin)

        steps = [
          el,
          ...steps
        ]

        if (i18n) {
          const label = getEventLabelGM(origin, i18n)
          const tag = {
            'eventCategory': 'como llegar',
            'eventAction': 'origen',
            'eventLabel': normalizeLowerCase(label),
            'language': i18n.locale,
            'airportID': getters[GETTERS.GET_AIRPORT_ID]
          }
          window.digitalChange('event', tag)
        }
      }
    } else {
      const destination = steps.find(x => x.isDestination)
      steps = steps.filter(x => !x.isDestination)

      steps = [
        ...steps,
        el,
        destination
      ].filter(x => x)
    }

    commit(MUTATIONS.SAVE_ROUTE, { steps, distance, duration, error: _error, defaultLevelBuilding: {} })
    commit(MUTATIONS.SAVE_ROUTE_MODE, routeMode !== undefined ? routeMode : !!steps.length)

    if (!reset && !_error && getters[GETTERS.GET_ORIGIN] && getters[GETTERS.GET_DESTINATION] && !getters[GETTERS.HAS_NEW_POINT]) {
      dispatch(ACTIONS.SET_DESTINATION_NAME)
      dispatch(ACTIONS.GET_ROUTE)
    }
  },
  [ACTIONS.REMOVE_STEP] ({ getters, commit, dispatch }, { index, hold }) {
    let steps = getters[GETTERS.GET_ROUTE].steps
    if (!hold) {
      steps.splice(index, 1)
    } else {
      steps[index] = {
        new: true
      }
    }

    commit(MUTATIONS.SAVE_ROUTE, { steps, route: [] })
    dispatch(ACTIONS.LOAD_ROUTE, {
      routeMode: true,
      reset: hold,
      step: hold && steps[index]
    })
  },
  [ACTIONS.CLEAR_ROUTE] ({ getters, commit, dispatch }) {
    commit(MUTATIONS.SAVE_ROUTE, {
      destinationName: undefined,
      steps: [],
      route: [],
      duration: 0,
      distance: 0
    })
    commit(MUTATIONS.SAVE_ROUTE_MODE)
    dispatch(ACTIONS.DELETE_LAYER)
    if (getters[GETTERS.GET_FLOOR]) {
      commit(MUTATIONS.SAVE_BBOX, getters[GETTERS.GET_FLOOR].bbox)
    }
  },
  [ACTIONS.MOVE_ROUTE] ({ commit, getters, dispatch }, payload) {
    let { steps } = payload
    const route = getters[GETTERS.GET_ROUTE]

    steps = steps.filter(x => x)
    if (steps.length > 1) {
      steps.forEach((step, index) => {
        if (step.isOrigin) {
          step.isOrigin = false
        }
        if (step.isDestination) {
          step.isDestination = false
        }
        if (!index) {
          step.isOrigin = true
        }
        if (index === steps.length - 1) {
          step.isDestination = true
        }
        step.geometry.coordinates[2] = hasFloor(step, route.z)
      })

      commit(MUTATIONS.SAVE_ROUTE, { steps })
      dispatch(ACTIONS.GET_ROUTE)
    }
  },
  async [ACTIONS.SET_DESTINATION_NAME] ({ commit }, payload) {
    commit(MUTATIONS.SAVE_DESTINATION_NAME_ROUTE, payload)
  },
  [ACTIONS.GET_ROUTE] ({ getters, dispatch, commit }) {
    commit(MUTATIONS.SAVE_ROUTE, { route: [], loading: true })
    dispatch(ACTIONS.SET_FEEDBACK)
    const route = getters[GETTERS.GET_ROUTE]
    return mapService.getRoute(
      getters[GETTERS.GET_AIRPORT_ID],
      {
        steps: route.steps.map(s => [...s.geometry.coordinates]),
        'profile': getters[GETTERS.GET_PMR_PROFILE] || undefined
      }
    )
      .then(response => {
        if (!response) {
          throw Error('No response')
        }

        if (getters[GETTERS.IS_ROUTE_MODE]) {
          if (response.steps.some(x => x.status === 'FAILED')) {
            throw Error('No possible')
          }

          const buildings = getters[GETTERS.GET_BUILDINGS]
          const origin = route.steps[0]

          const building = buildings.find(x => x.id === origin.properties.building)
          const floor = building.levels.find(x => x.id === origin.properties.level)

          dispatch(ACTIONS.LOAD_FLOOR, {
            floor,
            building
          })
          return dispatch(ACTIONS.GET_LINE_ROUTE, {
            route: response.steps,
            steps: route.steps,
            distance: response.total_distance,
            duration: response.total_time
          })
        }
      })
      .catch(error => {
        if (error.message !== 'Request canceled manually') {
          commit(MUTATIONS.SAVE_ROUTE, {
            error: error.message,
            loading: false
          })
        }
      })
  },
  [ACTIONS.GET_LINE_ROUTE] ({ getters, dispatch, commit }, { route, steps, distance, duration }) {
    const levels = {}
    let currentLevelData = []
    let currentLevel = -1
    let currentBuilding

    route.forEach((r, index) => {
      const currentStep = steps[index]
      const currentBuildingIndex = currentStep.properties.building
      r.instructions = serializeInstructions({
        currentBuildingIndex,
        instructions: r.instructions,
        features: r.geometry.features,
        stepIndex: index,
        buildings: getters[GETTERS.GET_BUILDINGS],
        z: getters[GETTERS.GET_ROUTE].z,
        steps: getters[GETTERS.GET_ROUTE].steps
      })

      currentBuilding = undefined

      setRouteBuildingFloor({
        currentStep,
        geometryFeatures: r.geometry.features,
        nextStep: steps[index + 1].geometry.coordinates,
        z: getters[GETTERS.GET_ROUTE].z,
        currentBuilding,
        currentLevelData,
        levels,
        currentLevel
      })

      commit(MUTATIONS.SAVE_ROUTE, {
        error: false,
        loading: false,
        route,
        distance,
        duration
      })

      const [geoJson, chunkJsonArrow] = buildLine({ levels, commit })

      const layer = LAYER_ROUTE
      const layerArrow = LAYER_ROUTE_ARROW

      layer.data = geoJson
      const { layerOpacity, arrowOpacity } = getFilterRoute(getters[GETTERS.GET_BUILDING], getters[GETTERS.GET_FLOOR])

      layer.layers[1].filter = [
        'all',
        ['==', ['get', 'level'], getters[GETTERS.GET_FLOOR].id],
        ['==', ['get', 'building'], getters[GETTERS.GET_BUILDING].id]
      ]

      layer.layers[0].paint['line-opacity'] = layerOpacity

      layerArrow.data = chunkJsonArrow
      layerArrow.layers[0].paint['icon-opacity'] = arrowOpacity

      dispatch(ACTIONS.ADD_LAYER, {
        ...layer
      })

      return dispatch(ACTIONS.ADD_LAYER, {
        ...layerArrow
      })
    })
  },
  [ACTIONS.LOAD_ACTIVE_FILTERS] ({ commit }, payload) {
    commit(MUTATIONS.SAVE_ACTIVE_FILTERS, payload)
  },
  [ACTIONS.LOAD_VIEW] ({ commit }, payload) {
    commit(MUTATIONS.SAVE_VIEW, payload)
  },
  [ACTIONS.ADD_LAYER] ({ state, commit }, layerConfig) {
    let existingLayerConfig = state.layerTree.find(l => l.id === layerConfig.id)
    if (existingLayerConfig) {
      commit(MUTATIONS.REMOVE_LAYER, layerConfig.id)
    }
    commit(MUTATIONS.SAVE_LAYER, layerConfig)
  },
  [ACTIONS.DELETE_LAYER] ({ commit }, id) {
    commit(MUTATIONS.REMOVE_LAYER, id)
  },
  [ACTIONS.SET_PMR_PROFILE] ({ getters, commit, dispatch }, profile) {
    commit(MUTATIONS.SAVE_PMR_PROFILE, profile)
    if (getters[GETTERS.IS_ROUTE_MODE]) {
      dispatch(ACTIONS.LOAD_ROUTE, {
        routeMode: true
      })
    }
  },
  [ACTIONS.SET_FEEDBACK] ({ commit }, payload) {
    const feedback = JSON.parse(localStorage.getItem('feedback') || '{}')
    if (payload) {
      feedback.answered = payload.answered
    }

    if (!feedback.answered) {
      feedback.count++

      if (feedback.count % 5 === 0) {
        commit(MUTATIONS.SAVE_FEEDBACK, true)
      }
    }

    localStorage.setItem('feedback', JSON.stringify(feedback))
  },
  [ACTIONS.SET_HOME_POSITION] ({ commit }, payload) {
    commit(MUTATIONS.SAVE_HOME_POSITION, payload)
  },
  async [ACTIONS.LOAD_ACCESS_CONTROL_TIME] ({ commit }, { code, pois }) {
    const response = await mapService.getAccessControlTime(code)

    response.forEach(control => {
      const poi = pois.features.find(p => p.properties.attrs.pat_id_poi.toString() === (control.idpoi || '').toString())
      if (poi) {
        const expires = control.expires || undefined
        const expired = new Date() > new Date(expires)
        poi.properties.lastWait = !expired ? control.last_wait_s : control.default_wait_s
        poi.properties.idAccessControl = control.id
      }
    })

    commit(MUTATIONS.SAVE_POIS_LIST, pois)
  }
}

function hasFloor (point, z) {
  let thirdCoordinate = point.geometry.coordinates[2]
  if (thirdCoordinate === undefined) {
    thirdCoordinate = point.properties.level * z
  }

  return thirdCoordinate
}

function pushDataInCurrentLevel (levels, currentBuilding, currentLevel, currentLevelData, coordinates) {
  const data = [
    ...currentLevelData,
    coordinates
  ]
  const index = levels[currentBuilding].findIndex(x => x.id === currentLevel)

  levels[currentBuilding][index].data.push(data)
}

function isLink (type, nextType, oldType) {
  const hasLink = type.includes('_link')
  return hasLink && (
    nextType === 'passport_control' ||
    nextType === 'security_control' ||
    nextType === 'oneway_opening' ||
    nextType === 'elevator' ||
    nextType === 'escalator_m' ||
    nextType === 'escalator_o' ||
    nextType === 'ramp' ||
    nextType === 'train' ||
    nextType === 'bus'
  ) && (
    oldType !== 'escalator_m' &&
    oldType !== 'escalator_o'
  )
}

function isAvoidAction (def, nextDef) {
  return def.action === nextDef.action && def.hidden
}

function isAvoidType (type, nextType) {
  return type === nextType && TYPES_HIDDEN.includes(type.toUpperCase())
}

function clearInstructions (instructions) {
  let instructionsCleared = instructions.filter((instruction, i) => {
    let res = true

    if (i !== instructions.length - 1) {
      const def = ROUTING_ACTIONS[instruction.type]

      res = !(def.removable)
    }

    if (i && i !== instructions.length - 1) {
      const oldStep = instructions[i - 1]
      const nextStep = instructions[i + 1]
      const type = instruction.tag.type
      const oldType = oldStep.tag.type
      const nextType = nextStep.tag.type

      if (isLink(type, nextType, oldType)) {
        nextStep.time = (LINKS.includes(oldType.toUpperCase()) ? oldStep.time : instruction.time) * 2
      }
    }

    return res
  })

  instructionsCleared = instructionsCleared.filter((instruction, i) => {
    let res = true

    if (i && i !== instructionsCleared.length - 1) {
      const def = ROUTING_ACTIONS[instruction.type]
      const oldStep = instructionsCleared[i - 1]
      const nextStep = instructionsCleared[i + 1]
      const type = instruction.tag.type
      const oldType = oldStep.tag.type
      const nextType = nextStep.tag.type
      const nextDef = ROUTING_ACTIONS[nextStep.type]

      const getIsAvoidType = isAvoidType(type, nextType)

      if (getIsAvoidType && i !== instructionsCleared.length - 2) {
        nextStep.geometryindex = instruction.geometryindex
      }

      res = !(isLink(type, nextType, oldType) || isAvoidAction(def, nextDef) || getIsAvoidType)
    }

    return res
  })

  return instructionsCleared
}

function serializeFeatures (features, instruction, oldGeometryIndex = 0) {
  const oldFeature = features[oldGeometryIndex]
  const oldBuilding = (oldFeature.properties || {}).building

  const diff = instruction.geometryindex - oldGeometryIndex

  if (diff) {
    const keys = [...Array(diff).keys()]
    keys.forEach(key => {
      const currentIndex = key + oldGeometryIndex
      const currentFeature = features[currentIndex]
      currentFeature.properties = {
        ...currentFeature.properties,
        building: oldBuilding
      }
    })
  }

  const feature = features[instruction.geometryindex]
  const building = (instruction.tag || {}).building_id

  feature.properties = {
    ...feature.properties,
    building
  }

  return features
}

function serializeInstructions ({ currentBuildingIndex, instructions, features, stepIndex, buildings, z, steps }) {
  const instructionsCleared = clearInstructions(instructions)
  let oldGeometryIndex
  const instructionsSerialized = instructionsCleared.map((originalInstruction, index) => {
    let instruction = { ...originalInstruction }
    serializeFeatures(features, instruction, oldGeometryIndex)

    oldGeometryIndex = instruction.geometryindex
    const nextInstruction = instructionsCleared[index + 1]
    if (nextInstruction) {
      const def = ROUTING_ACTIONS[instruction.type]
      const nextDef = ROUTING_ACTIONS[nextInstruction.type]
      const type = instruction.tag.type.toUpperCase()
      const nextType = nextInstruction.tag.type.toUpperCase()
      let iconType = (nextDef.types || {})[nextType]
      let time = instruction.time
      let hidden = def.hidden || TYPES_HIDDEN_NO_LINKS.includes(type)
      let direction = 'IN_FRONT'
      let msg = {}
      let securityControl = false
      let oneWay = false
      const nextBuildingIndex = nextInstruction.tag.building_id
      let changeBuilding = nextBuildingIndex && currentBuildingIndex !== nextBuildingIndex
      let operation = 'replace'
      let geometryindex = instruction.geometryindex
      let geometryindexFloor = instruction.geometryindex

      if (
        (nextDef.action === 'GO_UP' || nextDef.action === 'GO_DOWN') &&
        !(TYPES_HIDDEN_NO_LINKS.includes(nextType))) {
        operation = 'concat'
      }

      if (PUBLIC_TRANSPORT.includes(nextType)) {
        direction = nextInstruction.tag.desc.split('- ')[1]
        msg.label = `REPORT_SECURITY_CONTROL`
        msg.props = {
          // TODO: Change with dynamic time
          // duration: nextInstruction.time
          duration: nextType === 'TRAIN' ? 180 : 420
        }
        geometryindex = nextInstruction.geometryindex
      }

      if (PUBLIC_TRANSPORT.includes(type)) {
        geometryindexFloor = nextInstruction.geometryindex
      }

      if (SECURITY_CONTROLS.includes(nextType)) {
        securityControl = true
        msg.label = `REPORT_SECURITY_CONTROL`
        msg.props = {
          duration: nextInstruction.time
        }
        geometryindex = nextInstruction.geometryindex
      }

      if (ONEWAY.includes(nextType)) {
        oneWay = true
        msg.label = `ROUTE_ONEWAY_NO_RETURN`
        msg.props = {
          duration: nextInstruction.time
        }
        geometryindex = nextInstruction.geometryindex
      }

      const hasChangeAction = nextDef.changeAction || TYPES_HIDDEN_NO_LINKS.includes(nextType)

      const action = setAction({
        action: nextDef.action,
        hasChangeAction,
        nextAction: nextType,
        operation
      })

      if (action === 'FINISH_THE_ROUTE') {
        const indexMiddle = nextInstruction.geometryindex
        const indexStart = indexMiddle - 1

        const pointStart = features[indexStart].geometry.coordinates.slice(0, 2)
        const pointMiddle = features[indexMiddle].geometry.coordinates.slice(0, 2)
        const pointEnd = steps[stepIndex + 1].geometry.coordinates.slice(0, 2)
        const ang1 = bearing(pointStart, pointMiddle)
        const ang2 = bearing(pointMiddle, pointEnd)
        const ang = ang2 - ang1

        if ((ang > 30 && ang < 150) || (ang < -210 && ang > -330)) {
          direction = 'ON_THE_RIGHT'
        } else if ((ang < 33030 && ang > 210) || (ang < -30 && ang > -150)) {
          direction = 'ON_THE_LEFT'
        }

        iconType = (nextDef.types || {})[direction]
      }

      currentBuildingIndex = nextBuildingIndex
      const currentBuilding = getCurrentBuilding(nextBuildingIndex, buildings)
      const currentFloor = currentBuilding && getCurrentFloor(currentBuilding, features[geometryindexFloor + 1].geometry.coordinates[2], z)

      const icon = iconType || nextDef.defaultIcon

      instruction = {
        geometryindex,
        tag: nextInstruction.tag,
        icon,
        action: action,
        direction,
        hidden,
        changeFloor: def.changeFloor,
        changeBuilding,
        transport: nextInstruction.tag.name,
        building: currentBuilding,
        currentFloor: currentFloor,
        distance: instruction.distance,
        time,
        msg,
        securityControl,
        oneWay
      }
    }
    return instruction
  })

  return instructionsSerialized.slice(0, instructionsSerialized.length - 1)
}

function setAction ({ action, hasChangeAction, nextAction, operation = 'replace' }) {
  let newAction = action
  let changeAction = hasChangeAction

  if (changeAction) {
    if (operation === 'concat') {
      newAction += `_${nextAction}`
    } else {
      newAction = nextAction
    }
  }

  return newAction
}

function getCurrentFloor (building, step, z) {
  return (building.levels.find(x => x.id === (step / z)) || {}).name
}

function getCurrentBuilding (buildingId, buildings) {
  return buildings.find(x => x.id === buildingId)
}

function setRouteBuildingFloor ({
  currentStep,
  geometryFeatures,
  nextStep,
  z,
  currentBuilding,
  currentLevelData,
  levels,
  currentLevel
}) {
  const features = [
    currentStep,
    ...geometryFeatures
  ]
  features.forEach((step, i) => {
    const level = (step.geometry.coordinates[2] || 0) / (z || 1)
    const coordinates = step.geometry.coordinates.slice(0, 2)
    const newBuilding = step.properties || {}
    if (newBuilding.building !== currentBuilding) {
      if (currentLevelData.length) {
        pushDataInCurrentLevel(levels, currentBuilding, currentLevel, currentLevelData, coordinates)
      }
      currentLevelData = []
      currentBuilding = newBuilding.building

      if (!levels[currentBuilding]) {
        levels[currentBuilding] = []
        currentLevelData = []
      }
    }

    if (!levels[currentBuilding].some(x => x.id === level)) {
      levels[currentBuilding].push({
        id: level,
        data: []
      })
    }

    if (level !== currentLevel) {
      if (currentLevelData.length) {
        pushDataInCurrentLevel(levels, currentBuilding, currentLevel, currentLevelData, coordinates)
      }
      currentLevel = level
      currentLevelData = []
    }

    currentLevelData = [
      ...currentLevelData,
      ...[coordinates]
    ]

    if (i === features.length - 1) {
      pushDataInCurrentLevel(levels, currentBuilding, currentLevel, currentLevelData, nextStep)
    }
  })
}

function buildLine ({ levels, commit }) {
  const geoJson = {
    type: 'FeatureCollection',
    features: []
  }
  const chunkJsonArrow = {
    type: 'FeatureCollection',
    features: []
  }

  Object.entries(levels).forEach(([key, value]) => {
    value.forEach(x => {
      const level = parseInt(x.id)
      const building = !isNaN(key) && parseInt(key)
      commit(MUTATIONS.SAVE_DEFAULT_LEVEL_BUILDING, {
        [building]: level
      })

      geoJson.features.push(multiLineString(x.data, {
        level,
        ...(building && { building })
      }))
    })
  })

  geoJson.features.forEach(feature => {
    if (feature.properties.building) {
      const chunk = lineChunk(feature, ROUTE_ARROW_CHUNK).features
      chunk.forEach(x => {
        const start = x.geometry.coordinates[0]
        const middle = x.geometry.coordinates[1]
        const end = x.geometry.coordinates[1]
        chunkJsonArrow.features.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: x.geometry.coordinates[0]
          },
          properties: {
            level: feature.properties.level,
            building: feature.properties.building,
            angle: angle(start, middle, end)
          }
        })
      })
    }
  })

  commit(MUTATIONS.SAVE_BBOX, bbox(geoJson))

  return [geoJson, chunkJsonArrow]
}
